import type {
  WatchSignalDefinition,
  WatchSignalOperator,
  WatchSignalParameterDescriptor,
} from "./watchSignalCatalog";
import { canonicalRatioToPercent } from "./watchRatioPresentation";

interface WatchSignalReleasePresenterBase {
  /** Exact immutable identity. */
  readonly identity:
    | "underlying.price:1"
    | "underlying.price_change.prior_regular_close:1"
    | "underlying.price_change.regular_session_open:1"
    | "underlying.completed_regular_close.relative_to_sma:1"
    | "underlying.completed_regular_close.crosses_sma:1"
    | "underlying.completed_regular_close.relative_to_ema:1"
    | "underlying.completed_regular_close.crosses_ema:1"
    | "underlying.sma.crosses_sma:1"
    | "underlying.ema.crosses_ema:1"
    | "underlying.rsi.relative_to_level:1"
    | "underlying.rsi.crosses_level:1"
    | "underlying.macd.line.crosses_signal:1"
    | "underlying.macd.line.crosses_zero:1"
    | "underlying.roc.relative_to_level:1"
    | "underlying.roc.crosses_level:1"
    | "underlying.atr.completed_daily.14.relative_to_level:1"
    | "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level:1"
    | "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level:1"
    | "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level:1"
    | "option_contract.implied_volatility.relative_to_level:1"
    | "option_contract.implied_volatility.crosses_level:1"
    | "option_contract.nbbo.relative_spread.relative_to_level:1"
    | "option_contract.nbbo.relative_spread.crosses_level:1"
    | "option_contract.regular_session.volume.relative_to_level:1"
    | "option_contract.regular_session.volume.crosses_level:1"
    | "option_contract.open_interest.relative_to_level:1"
    | "option_contract.open_interest.crosses_level:1"
    | "options_chain.atm_iv.standardized_30d.relative_to_level:1"
    | "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level:1"
    | "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level:1"
    | "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level:1"
    | "options_chain.expected_move.standardized_30d.relative_to_level:1"
    | "options_chain.atm_quote_coverage.standardized_30d.relative_to_level:1"
    | "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level:1"
    | "options_chain.put_call_open_interest.standardized_7_45d.relative_to_level:1"
    | "options_chain.open_interest_concentration.top5.standardized_7_45d.relative_to_level:1";
  /** Customer-facing control family. */
  readonly kind:
    | "EXACT_PRICE"
    | "PERCENTAGE_MOVE"
    | "MOVING_AVERAGE_RELATIONSHIP"
    | "MOVING_AVERAGE_CROSSOVER"
    | "MOMENTUM_RSI"
    | "MOMENTUM_MACD"
    | "MOMENTUM_ROC"
    | "COMPLETED_DAILY_VOLATILITY"
    | "EXACT_OPTION_CONTRACT"
    | "STANDARDIZED_OPTION_CHAIN";
}

/** Moving-average calculation selected explicitly by the customer. */
export type MovingAverageType = "SMA" | "EMA";
/** Reviewed completed-daily windows. */
export type MovingAverageWindow = "20" | "50" | "200";
/** Reviewed fast/slow pairs; arbitrary window combinations are not authorable. */
export type MovingAverageWindowPair = "20_50" | "20_200" | "50_200";
/** Plain-language direction relative to the right-hand value. */
export type MovingAverageDirection = "ABOVE" | "BELOW";
/** State and event semantics are separate immutable contracts. */
export type MovingAverageTriggerMode = "STATE" | "CROSSING";
export type CompletedDailyVolatilityMeasurement =
  | "ATR14"
  | "BOLLINGER_PERCENT_B_20_2"
  | "BOLLINGER_BANDWIDTH_20_2"
  | "REALIZED_VOLATILITY_20D";
export type ExactOptionContractMeasurement =
  | "IMPLIED_VOLATILITY"
  | "NBBO_RELATIVE_SPREAD"
  | "REGULAR_SESSION_VOLUME"
  | "OPEN_INTEREST";
export type StandardizedOptionChainMeasurement =
  | "ATM_IV_30D"
  | "IV_RV_SPREAD_30D_20D"
  | "RISK_REVERSAL_30D_25_DELTA"
  | "ATM_IV_TERM_SLOPE_60D_MINUS_30D"
  | "EXPECTED_MOVE_30D"
  | "ATM_QUOTE_COVERAGE_30D"
  | "ATM_MEDIAN_RELATIVE_SPREAD_30D"
  | "PUT_CALL_OPEN_INTEREST_7_45D"
  | "OPEN_INTEREST_CONCENTRATION_TOP5_7_45D";

/** Customer controls for the exact-price release. */
export interface ExactPriceSignalPresenter extends WatchSignalReleasePresenterBase {
  readonly identity: "underlying.price:1";
  readonly kind: "EXACT_PRICE";
  /** Customer label for the scalar threshold. */
  readonly thresholdLabel: "Target price";
  /** Available inclusive comparisons and their customer copy. */
  readonly operatorChoices: ReadonlyArray<{
    readonly operator: "gte" | "lte";
    readonly label: string;
  }>;
}

/** One immutable reference choice grouped under the Percentage move concept. */
export interface PercentageMoveSignalPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.price_change.prior_regular_close:1"
    | "underlying.price_change.regular_session_open:1";
  readonly kind: "PERCENTAGE_MOVE";
  readonly referenceKind:
    | "PRIOR_REGULAR_CLOSE"
    | "REGULAR_SESSION_OPEN";
  readonly referenceLabel:
    | "Previous regular-session close"
    | "Today’s regular-session open";
  readonly referencePhrase:
    | "the previous regular-session close"
    | "today's regular-session open";
}

/** One reviewed completed-close versus moving-average leaf. */
export interface MovingAverageRelationshipPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.completed_regular_close.relative_to_sma:1"
    | "underlying.completed_regular_close.crosses_sma:1"
    | "underlying.completed_regular_close.relative_to_ema:1"
    | "underlying.completed_regular_close.crosses_ema:1";
  readonly kind: "MOVING_AVERAGE_RELATIONSHIP";
  readonly averageType: MovingAverageType;
  readonly triggerMode: MovingAverageTriggerMode;
}

/** One reviewed fast-versus-slow moving-average crossover leaf. */
export interface MovingAverageCrossoverPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.sma.crosses_sma:1"
    | "underlying.ema.crosses_ema:1";
  readonly kind: "MOVING_AVERAGE_CROSSOVER";
  readonly averageType: MovingAverageType;
}

/** One reviewed RSI(14) state or crossing leaf. */
export interface MomentumRsiPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.rsi.relative_to_level:1"
    | "underlying.rsi.crosses_level:1";
  readonly kind: "MOMENTUM_RSI";
  readonly triggerMode: MovingAverageTriggerMode;
}

/** One reviewed MACD(12,26,9) crossover subject. */
export interface MomentumMacdPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.macd.line.crosses_signal:1"
    | "underlying.macd.line.crosses_zero:1";
  readonly kind: "MOMENTUM_MACD";
  readonly comparison: "SIGNAL_LINE" | "ZERO";
}

/** One reviewed ROC(14) state or crossing leaf. */
export interface MomentumRocPresenter extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.roc.relative_to_level:1"
    | "underlying.roc.crosses_level:1";
  readonly kind: "MOMENTUM_ROC";
  readonly triggerMode: MovingAverageTriggerMode;
}

/** One reviewed state-only completed-daily volatility measurement. */
export interface CompletedDailyVolatilityPresenter
  extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "underlying.atr.completed_daily.14.relative_to_level:1"
    | "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level:1"
    | "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level:1"
    | "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level:1";
  readonly kind: "COMPLETED_DAILY_VOLATILITY";
  readonly measurement: CompletedDailyVolatilityMeasurement;
  readonly thresholdParameter: "level_usd" | "level";
}

/** One reviewed exact-contract state or crossing measurement. */
export interface ExactOptionContractPresenter
  extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "option_contract.implied_volatility.relative_to_level:1"
    | "option_contract.implied_volatility.crosses_level:1"
    | "option_contract.nbbo.relative_spread.relative_to_level:1"
    | "option_contract.nbbo.relative_spread.crosses_level:1"
    | "option_contract.regular_session.volume.relative_to_level:1"
    | "option_contract.regular_session.volume.crosses_level:1"
    | "option_contract.open_interest.relative_to_level:1"
    | "option_contract.open_interest.crosses_level:1";
  readonly kind: "EXACT_OPTION_CONTRACT";
  readonly measurement: ExactOptionContractMeasurement;
  readonly triggerMode: MovingAverageTriggerMode;
  readonly thresholdParameter: "level" | "contracts";
}

/** One reviewed underlying-level standardized option-chain measurement. */
export interface StandardizedOptionChainPresenter
  extends WatchSignalReleasePresenterBase {
  readonly identity:
    | "options_chain.atm_iv.standardized_30d.relative_to_level:1"
    | "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level:1"
    | "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level:1"
    | "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level:1"
    | "options_chain.expected_move.standardized_30d.relative_to_level:1"
    | "options_chain.atm_quote_coverage.standardized_30d.relative_to_level:1"
    | "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level:1"
    | "options_chain.put_call_open_interest.standardized_7_45d.relative_to_level:1"
    | "options_chain.open_interest_concentration.top5.standardized_7_45d.relative_to_level:1";
  readonly kind: "STANDARDIZED_OPTION_CHAIN";
  readonly measurement: StandardizedOptionChainMeasurement;
}

export type WatchSignalReleasePresenter =
  | ExactPriceSignalPresenter
  | PercentageMoveSignalPresenter
  | MovingAverageRelationshipPresenter
  | MovingAverageCrossoverPresenter
  | MomentumRsiPresenter
  | MomentumMacdPresenter
  | MomentumRocPresenter
  | CompletedDailyVolatilityPresenter
  | ExactOptionContractPresenter
  | StandardizedOptionChainPresenter;

const PRICE_V1_PRESENTER: ExactPriceSignalPresenter = {
  identity: "underlying.price:1",
  kind: "EXACT_PRICE",
  thresholdLabel: "Target price",
  operatorChoices: [
    { operator: "gte", label: "Reaches or rises above" },
    { operator: "lte", label: "Reaches or falls below" },
  ],
};

const PRIOR_CLOSE_PERCENTAGE_V1_PRESENTER: PercentageMoveSignalPresenter = {
  identity: "underlying.price_change.prior_regular_close:1",
  kind: "PERCENTAGE_MOVE",
  referenceKind: "PRIOR_REGULAR_CLOSE",
  referenceLabel: "Previous regular-session close",
  referencePhrase: "the previous regular-session close",
};

const SESSION_OPEN_PERCENTAGE_V1_PRESENTER: PercentageMoveSignalPresenter = {
  identity: "underlying.price_change.regular_session_open:1",
  kind: "PERCENTAGE_MOVE",
  referenceKind: "REGULAR_SESSION_OPEN",
  referenceLabel: "Today’s regular-session open",
  referencePhrase: "today's regular-session open",
};

const MOVING_AVERAGE_PRESENTERS: Readonly<Record<string,
MovingAverageRelationshipPresenter | MovingAverageCrossoverPresenter>> = {
  "underlying.completed_regular_close.relative_to_sma:1": {
    identity: "underlying.completed_regular_close.relative_to_sma:1",
    kind: "MOVING_AVERAGE_RELATIONSHIP",
    averageType: "SMA",
    triggerMode: "STATE",
  },
  "underlying.completed_regular_close.crosses_sma:1": {
    identity: "underlying.completed_regular_close.crosses_sma:1",
    kind: "MOVING_AVERAGE_RELATIONSHIP",
    averageType: "SMA",
    triggerMode: "CROSSING",
  },
  "underlying.completed_regular_close.relative_to_ema:1": {
    identity: "underlying.completed_regular_close.relative_to_ema:1",
    kind: "MOVING_AVERAGE_RELATIONSHIP",
    averageType: "EMA",
    triggerMode: "STATE",
  },
  "underlying.completed_regular_close.crosses_ema:1": {
    identity: "underlying.completed_regular_close.crosses_ema:1",
    kind: "MOVING_AVERAGE_RELATIONSHIP",
    averageType: "EMA",
    triggerMode: "CROSSING",
  },
  "underlying.sma.crosses_sma:1": {
    identity: "underlying.sma.crosses_sma:1",
    kind: "MOVING_AVERAGE_CROSSOVER",
    averageType: "SMA",
  },
  "underlying.ema.crosses_ema:1": {
    identity: "underlying.ema.crosses_ema:1",
    kind: "MOVING_AVERAGE_CROSSOVER",
    averageType: "EMA",
  },
};

const MOMENTUM_PRESENTERS: Readonly<Record<string,
MomentumRsiPresenter | MomentumMacdPresenter | MomentumRocPresenter>> = {
  "underlying.rsi.relative_to_level:1": {
    identity: "underlying.rsi.relative_to_level:1",
    kind: "MOMENTUM_RSI",
    triggerMode: "STATE",
  },
  "underlying.rsi.crosses_level:1": {
    identity: "underlying.rsi.crosses_level:1",
    kind: "MOMENTUM_RSI",
    triggerMode: "CROSSING",
  },
  "underlying.macd.line.crosses_signal:1": {
    identity: "underlying.macd.line.crosses_signal:1",
    kind: "MOMENTUM_MACD",
    comparison: "SIGNAL_LINE",
  },
  "underlying.macd.line.crosses_zero:1": {
    identity: "underlying.macd.line.crosses_zero:1",
    kind: "MOMENTUM_MACD",
    comparison: "ZERO",
  },
  "underlying.roc.relative_to_level:1": {
    identity: "underlying.roc.relative_to_level:1",
    kind: "MOMENTUM_ROC",
    triggerMode: "STATE",
  },
  "underlying.roc.crosses_level:1": {
    identity: "underlying.roc.crosses_level:1",
    kind: "MOMENTUM_ROC",
    triggerMode: "CROSSING",
  },
};

const VOLATILITY_PRESENTERS: Readonly<Record<string, CompletedDailyVolatilityPresenter>> = {
  "underlying.atr.completed_daily.14.relative_to_level:1": {
    identity: "underlying.atr.completed_daily.14.relative_to_level:1",
    kind: "COMPLETED_DAILY_VOLATILITY",
    measurement: "ATR14",
    thresholdParameter: "level_usd",
  },
  "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level:1": {
    identity: "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level:1",
    kind: "COMPLETED_DAILY_VOLATILITY",
    measurement: "BOLLINGER_PERCENT_B_20_2",
    thresholdParameter: "level",
  },
  "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level:1": {
    identity: "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level:1",
    kind: "COMPLETED_DAILY_VOLATILITY",
    measurement: "BOLLINGER_BANDWIDTH_20_2",
    thresholdParameter: "level",
  },
  "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level:1": {
    identity: "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level:1",
    kind: "COMPLETED_DAILY_VOLATILITY",
    measurement: "REALIZED_VOLATILITY_20D",
    thresholdParameter: "level",
  },
};

const EXACT_OPTION_CONTRACT_PRESENTERS: Readonly<Record<string,
ExactOptionContractPresenter>> = {
  "option_contract.implied_volatility.relative_to_level:1": {
    identity: "option_contract.implied_volatility.relative_to_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "IMPLIED_VOLATILITY",
    triggerMode: "STATE",
    thresholdParameter: "level",
  },
  "option_contract.implied_volatility.crosses_level:1": {
    identity: "option_contract.implied_volatility.crosses_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "IMPLIED_VOLATILITY",
    triggerMode: "CROSSING",
    thresholdParameter: "level",
  },
  "option_contract.nbbo.relative_spread.relative_to_level:1": {
    identity: "option_contract.nbbo.relative_spread.relative_to_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "NBBO_RELATIVE_SPREAD",
    triggerMode: "STATE",
    thresholdParameter: "level",
  },
  "option_contract.nbbo.relative_spread.crosses_level:1": {
    identity: "option_contract.nbbo.relative_spread.crosses_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "NBBO_RELATIVE_SPREAD",
    triggerMode: "CROSSING",
    thresholdParameter: "level",
  },
  "option_contract.regular_session.volume.relative_to_level:1": {
    identity: "option_contract.regular_session.volume.relative_to_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "REGULAR_SESSION_VOLUME",
    triggerMode: "STATE",
    thresholdParameter: "contracts",
  },
  "option_contract.regular_session.volume.crosses_level:1": {
    identity: "option_contract.regular_session.volume.crosses_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "REGULAR_SESSION_VOLUME",
    triggerMode: "CROSSING",
    thresholdParameter: "contracts",
  },
  "option_contract.open_interest.relative_to_level:1": {
    identity: "option_contract.open_interest.relative_to_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "OPEN_INTEREST",
    triggerMode: "STATE",
    thresholdParameter: "contracts",
  },
  "option_contract.open_interest.crosses_level:1": {
    identity: "option_contract.open_interest.crosses_level:1",
    kind: "EXACT_OPTION_CONTRACT",
    measurement: "OPEN_INTEREST",
    triggerMode: "CROSSING",
    thresholdParameter: "contracts",
  },
};

const STANDARDIZED_OPTION_CHAIN_PRESENTERS: Readonly<Record<string,
StandardizedOptionChainPresenter>> = {
  "options_chain.atm_iv.standardized_30d.relative_to_level:1": {
    identity: "options_chain.atm_iv.standardized_30d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "ATM_IV_30D",
  },
  "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level:1": {
    identity: "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "IV_RV_SPREAD_30D_20D",
  },
  "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level:1": {
    identity: "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "RISK_REVERSAL_30D_25_DELTA",
  },
  "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level:1": {
    identity: "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "ATM_IV_TERM_SLOPE_60D_MINUS_30D",
  },
  "options_chain.expected_move.standardized_30d.relative_to_level:1": {
    identity: "options_chain.expected_move.standardized_30d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "EXPECTED_MOVE_30D",
  },
  "options_chain.atm_quote_coverage.standardized_30d.relative_to_level:1": {
    identity: "options_chain.atm_quote_coverage.standardized_30d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "ATM_QUOTE_COVERAGE_30D",
  },
  "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level:1": {
    identity: "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "ATM_MEDIAN_RELATIVE_SPREAD_30D",
  },
  "options_chain.put_call_open_interest.standardized_7_45d.relative_to_level:1": {
    identity: "options_chain.put_call_open_interest.standardized_7_45d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "PUT_CALL_OPEN_INTEREST_7_45D",
  },
  "options_chain.open_interest_concentration.top5.standardized_7_45d.relative_to_level:1": {
    identity: "options_chain.open_interest_concentration.top5.standardized_7_45d.relative_to_level:1",
    kind: "STANDARDIZED_OPTION_CHAIN",
    measurement: "OPEN_INTEREST_CONCENTRATION_TOP5_7_45D",
  },
};

const VOLATILITY_CUSTOMER_COPY: Readonly<Record<string, WatchSignalDefinition["customer"]>> = {
  "underlying.atr.completed_daily.14.relative_to_level": {
    name: "14-day average true range",
    description: "Alert when the completed-daily 14-day average true range reaches your chosen dollar amount.",
  },
  "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level": {
    name: "Bollinger position",
    description: "Alert when the completed-daily close's Bollinger %B reaches your chosen level within or beyond its 20-day bands.",
  },
  "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level": {
    name: "Bollinger band width",
    description: "Alert when completed-daily Bollinger band width reaches your chosen ratio of the 20-day middle band.",
  },
  "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level": {
    name: "20-day realized volatility",
    description: "Alert when 20-day annualized close-to-close realized volatility reaches your chosen level.",
  },
};

const EXACT_OPTION_CONTRACT_CUSTOMER_COPY: Readonly<Record<string,
WatchSignalDefinition["customer"]>> = {
  "option_contract.implied_volatility.relative_to_level": {
    name: "Contract implied volatility",
    description: "Monitor the exact selected option contract's annualized implied volatility.",
  },
  "option_contract.implied_volatility.crosses_level": {
    name: "Contract implied-volatility crossing",
    description: "Monitor a strict crossing by the exact selected option contract's annualized implied volatility.",
  },
  "option_contract.nbbo.relative_spread.relative_to_level": {
    name: "Contract relative bid/ask spread",
    description: "Monitor the exact selected option contract's bid/ask spread as a ratio of its midpoint.",
  },
  "option_contract.nbbo.relative_spread.crosses_level": {
    name: "Contract relative-spread crossing",
    description: "Monitor a strict crossing by the exact selected option contract's midpoint-relative bid/ask spread.",
  },
  "option_contract.regular_session.volume.relative_to_level": {
    name: "Contract session volume",
    description: "Monitor reported regular-session volume for the exact selected option contract.",
  },
  "option_contract.regular_session.volume.crosses_level": {
    name: "Contract session-volume crossing",
    description: "Monitor a strict same-session volume crossing for the exact selected option contract.",
  },
  "option_contract.open_interest.relative_to_level": {
    name: "Contract open interest",
    description: "Monitor officially dated open interest for the exact selected option contract.",
  },
  "option_contract.open_interest.crosses_level": {
    name: "Contract open-interest crossing",
    description: "Monitor a strict crossing between independently dated open-interest observations for the exact selected option contract.",
  },
};

const STANDARDIZED_OPTION_CHAIN_CUSTOMER_COPY: Readonly<Record<string,
WatchSignalDefinition["customer"]>> = {
  "options_chain.atm_iv.standardized_30d.relative_to_level": {
    name: "30-day implied volatility",
    description: "Monitor the options market's standardized 30-day implied-volatility level.",
  },
  "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level": {
    name: "Implied versus recent volatility",
    description: "Compare standardized 30-day implied volatility with completed-session 20-day realized volatility.",
  },
  "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level": {
    name: "30-day put/call skew",
    description: "Compare standardized 25-delta put and call implied volatility at a 30-day horizon.",
  },
  "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level": {
    name: "30-to-60-day IV slope",
    description: "Compare standardized 60-day and 30-day at-the-money implied volatility.",
  },
  "options_chain.expected_move.standardized_30d.relative_to_level": {
    name: "30-day expected move",
    description: "Monitor a standardized one-standard-deviation 30-day movement estimate; it is not a forecast.",
  },
  "options_chain.atm_quote_coverage.standardized_30d.relative_to_level": {
    name: "Options quote coverage",
    description: "Measure the share of the standardized near-ATM cohort with valid two-sided quotes.",
  },
  "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level": {
    name: "Typical near-ATM spread",
    description: "Measure the equal-weight median relative spread across a coverage-qualified near-ATM cohort.",
  },
  "options_chain.put_call_open_interest.standardized_7_45d.relative_to_level": {
    name: "Put/call open interest",
    description: "Compare prior-session reported put and call open interest across the current standardized near-term cohort.",
  },
  "options_chain.open_interest_concentration.top5.standardized_7_45d.relative_to_level": {
    name: "Open-interest concentration",
    description: "Measure the share of prior-session reported open interest held by the current cohort's five largest contracts.",
  },
};

const MOMENTUM_CUSTOMER_COPY: Readonly<Record<string, WatchSignalDefinition["customer"]>> = {
  "underlying.rsi.relative_to_level": {
    name: "RSI level",
    description: "Alert when completed-daily RSI is at or beyond your selected level.",
  },
  "underlying.rsi.crosses_level": {
    name: "RSI crosses a level",
    description: "Alert after completed-daily RSI strictly crosses your selected level.",
  },
  "underlying.macd.line.crosses_signal": {
    name: "MACD line crosses signal line",
    description: "Alert after the completed-daily MACD line strictly crosses its signal line.",
  },
  "underlying.macd.line.crosses_zero": {
    name: "MACD line crosses zero",
    description: "Alert after the completed-daily MACD line strictly crosses zero.",
  },
  "underlying.roc.relative_to_level": {
    name: "14-day rate of change",
    description: "Alert when the completed-daily 14-day price change is at or beyond your selected percentage.",
  },
  "underlying.roc.crosses_level": {
    name: "14-day rate of change crosses a level",
    description: "Alert after the completed-daily 14-day price change strictly crosses your selected percentage.",
  },
};

const TREND_CUSTOMER_COPY: Readonly<Record<string, WatchSignalDefinition["customer"]>> = {
  "underlying.completed_regular_close.relative_to_sma": {
    name: "Price vs. simple moving average",
    description: "Alert when the completed daily close is above or below a simple moving average.",
  },
  "underlying.completed_regular_close.crosses_sma": {
    name: "Price crosses simple moving average",
    description: "Alert only after the completed daily close crosses a simple moving average.",
  },
  "underlying.completed_regular_close.relative_to_ema": {
    name: "Price vs. exponential moving average",
    description: "Alert when the completed daily close is above or below an exponential moving average.",
  },
  "underlying.completed_regular_close.crosses_ema": {
    name: "Price crosses exponential moving average",
    description: "Alert only after the completed daily close crosses an exponential moving average.",
  },
  "underlying.sma.crosses_sma": {
    name: "Simple moving-average crossover",
    description: "Alert only after a faster simple moving average crosses a slower one.",
  },
  "underlying.ema.crosses_ema": {
    name: "Exponential moving-average crossover",
    description: "Alert only after a faster exponential moving average crosses a slower one.",
  },
};

function exactMembers<T extends string>(
  values: readonly T[],
  expected: readonly T[],
) {
  return values.length === expected.length
    && expected.every((value) => values.includes(value));
}

function hasNoOptionalDescriptorFields(parameter: WatchSignalParameterDescriptor) {
  return parameter.unit === undefined
    && parameter.minimum === undefined
    && parameter.maximum === undefined
    && parameter.max_decimal_places === undefined
    && parameter.allowed_values === undefined
    && parameter.default_value === undefined;
}

function hasExactEnumDescriptor(
  parameter: WatchSignalParameterDescriptor | undefined,
  parameterId: string,
  allowedValues: readonly string[],
) {
  return parameter?.parameter_id === parameterId
    && parameter.kind === "ENUM"
    && parameter.required
    && parameter.unit === undefined
    && parameter.minimum === undefined
    && parameter.maximum === undefined
    && parameter.max_decimal_places === undefined
    && parameter.default_value === undefined
    && parameter.allowed_values !== undefined
    && exactMembers(parameter.allowed_values, allowedValues);
}

function isExactTickerDescriptor(parameter: WatchSignalParameterDescriptor | undefined) {
  return parameter?.parameter_id === "ticker"
    && parameter.kind === "SYMBOL"
    && parameter.required
    && hasNoOptionalDescriptorFields(parameter);
}

function isExactOptionContractDescriptor(
  parameter: WatchSignalParameterDescriptor | undefined,
) {
  return parameter?.parameter_id === "option_contract"
    // OPTION_CONTRACT is intentionally not decoded by the public catalog yet.
    // This reviewed presenter remains dormant until that schema and the server
    // capability are explicitly released together.
    && (parameter.kind as string) === "OPTION_CONTRACT"
    && parameter.required
    && hasNoOptionalDescriptorFields(parameter);
}

function hasExactNumericDescriptor(
  parameter: WatchSignalParameterDescriptor | undefined,
  expected: {
    parameterId: string;
    kind: "DECIMAL" | "INTEGER";
    unit: WatchSignalDefinition["unit"];
    minimum: string;
    maximum: string;
    maxDecimalPlaces?: number;
  },
) {
  return parameter?.parameter_id === expected.parameterId
    && parameter.kind === expected.kind
    && parameter.required
    && parameter.unit === expected.unit
    && parameter.minimum === expected.minimum
    && parameter.maximum === expected.maximum
    && parameter.max_decimal_places === expected.maxDecimalPlaces
    && parameter.allowed_values === undefined
    && parameter.default_value === undefined;
}

function isReviewedPriceV1(definition: WatchSignalDefinition) {
  if (definition.signal_id !== "underlying.price"
    || definition.definition_version !== 1
    || definition.family !== "PRICE"
    || definition.unit !== "USD_PER_SHARE"
    || !exactMembers(definition.eligible_jobs, ["MARKET"])
    || !exactMembers(definition.subject_scopes, ["UNDERLYING"])
    || !exactMembers<WatchSignalOperator>(definition.operators, ["gte", "lte"])
    || definition.parameters.length !== 2) {
    return false;
  }
  const ticker = definition.parameters.find((parameter) => parameter.parameter_id === "ticker");
  const threshold = definition.parameters.find((parameter) => parameter.parameter_id === "threshold");
  return ticker?.kind === "SYMBOL"
    && ticker.required
    && hasNoOptionalDescriptorFields(ticker)
    && threshold?.kind === "DECIMAL"
    && threshold.required
    && threshold.unit === "USD_PER_SHARE"
    && threshold.minimum === "0.000001"
    && threshold.maximum === "100000000"
    && threshold.max_decimal_places === 6
    && threshold.allowed_values === undefined
    && threshold.default_value === undefined;
}

function isReviewedPercentageMoveV1(definition: WatchSignalDefinition) {
  if (definition.definition_version !== 1
    || ![
      "underlying.price_change.prior_regular_close",
      "underlying.price_change.regular_session_open",
    ].includes(definition.signal_id)
    || definition.family !== "PRICE"
    || definition.unit !== "PERCENT"
    || !exactMembers(definition.eligible_jobs, ["MARKET"])
    || !exactMembers(definition.subject_scopes, ["UNDERLYING"])
    || !exactMembers<WatchSignalOperator>(definition.operators, ["gte", "lte"])
    || definition.parameters.length !== 3) {
    return false;
  }
  const ticker = definition.parameters.find((parameter) => parameter.parameter_id === "ticker");
  const direction = definition.parameters.find(
    (parameter) => parameter.parameter_id === "direction",
  );
  const magnitude = definition.parameters.find(
    (parameter) => parameter.parameter_id === "magnitude_percent",
  );
  return ticker?.kind === "SYMBOL"
    && ticker.required
    && hasNoOptionalDescriptorFields(ticker)
    && hasExactEnumDescriptor(direction, "direction", ["UP", "DOWN"])
    && magnitude?.kind === "DECIMAL"
    && magnitude.required
    && magnitude.unit === "PERCENT"
    && magnitude.minimum === "0.000001"
    && magnitude.maximum === "100"
    && magnitude.max_decimal_places === 6
    && magnitude.allowed_values === undefined
    && magnitude.default_value === undefined;
}

function isReviewedMovingAverageV1(definition: WatchSignalDefinition) {
  const identity = `${definition.signal_id}:${definition.definition_version}`;
  const presenter = MOVING_AVERAGE_PRESENTERS[identity];
  const expectedCopy = TREND_CUSTOMER_COPY[definition.signal_id];
  if (!presenter
    || !expectedCopy
    || definition.definition_version !== 1
    || definition.family !== "TREND"
    || definition.unit !== "DECIMAL"
    || !exactMembers(definition.eligible_jobs, ["MARKET"])
    || !exactMembers(definition.subject_scopes, ["UNDERLYING"])
    || !exactMembers<WatchSignalOperator>(definition.operators, ["gt", "lt"])
    || definition.customer.name !== expectedCopy.name
    || definition.customer.description !== expectedCopy.description
    || definition.parameters.length !== 3
    || !isExactTickerDescriptor(definition.parameters[0])
    || !hasExactEnumDescriptor(
      definition.parameters[1],
      "direction",
      ["ABOVE", "BELOW"],
    )) {
    return false;
  }
  return presenter.kind === "MOVING_AVERAGE_RELATIONSHIP"
    ? hasExactEnumDescriptor(definition.parameters[2], "window", ["20", "50", "200"])
    : hasExactEnumDescriptor(
      definition.parameters[2],
      "window_pair",
      ["20_50", "20_200", "50_200"],
    );
}

function hasExactDecimalDescriptor(
  parameter: WatchSignalParameterDescriptor | undefined,
  parameterId: string,
  unit: "INDEX_POINTS" | "PERCENT",
  minimum: string,
  maximum: string,
) {
  return parameter?.parameter_id === parameterId
    && parameter.kind === "DECIMAL"
    && parameter.required
    && parameter.unit === unit
    && parameter.minimum === minimum
    && parameter.maximum === maximum
    && parameter.max_decimal_places === 6
    && parameter.allowed_values === undefined
    && parameter.default_value === undefined;
}

function isReviewedMomentumV1(definition: WatchSignalDefinition) {
  const identity = `${definition.signal_id}:${definition.definition_version}`;
  const presenter = MOMENTUM_PRESENTERS[identity];
  const expectedCopy = MOMENTUM_CUSTOMER_COPY[definition.signal_id];
  if (!presenter
    || !expectedCopy
    || definition.definition_version !== 1
    || definition.family !== "MOMENTUM"
    || !exactMembers(definition.eligible_jobs, ["MARKET"])
    || !exactMembers(definition.subject_scopes, ["UNDERLYING"])
    || !exactMembers<WatchSignalOperator>(
      definition.operators,
      (presenter.kind === "MOMENTUM_RSI" || presenter.kind === "MOMENTUM_ROC")
        && presenter.triggerMode === "STATE" ? ["gte", "lte"] : ["gt", "lt"],
    )
    || definition.customer.name !== expectedCopy.name
    || definition.customer.description !== expectedCopy.description
    || !isExactTickerDescriptor(definition.parameters[0])
    || !hasExactEnumDescriptor(definition.parameters[1], "direction", ["ABOVE", "BELOW"])) {
    return false;
  }
  if (presenter.kind === "MOMENTUM_MACD") {
    return definition.unit === "USD_PER_SHARE" && definition.parameters.length === 2;
  }
  if (presenter.kind === "MOMENTUM_RSI") {
    return definition.unit === "INDEX_POINTS"
      && definition.parameters.length === 3
      && hasExactDecimalDescriptor(
        definition.parameters[2], "level", "INDEX_POINTS", "0.000001", "99.999999",
      );
  }
  return definition.unit === "PERCENT"
    && definition.parameters.length === 3
    && hasExactDecimalDescriptor(
      definition.parameters[2], "level_percent", "PERCENT", "-100", "1000",
    );
}

function isReviewedVolatilityV1(definition: WatchSignalDefinition) {
  const identity = `${definition.signal_id}:${definition.definition_version}`;
  const presenter = VOLATILITY_PRESENTERS[identity];
  const expectedCopy = VOLATILITY_CUSTOMER_COPY[definition.signal_id];
  if (!presenter
    || !expectedCopy
    || definition.definition_version !== 1
    || definition.family !== "VOLATILITY"
    || !exactMembers(definition.eligible_jobs, ["MARKET"])
    || !exactMembers(definition.subject_scopes, ["UNDERLYING"])
    || !exactMembers<WatchSignalOperator>(definition.operators, ["gte", "lte"])
    || definition.customer.name !== expectedCopy.name
    || definition.customer.description !== expectedCopy.description
    || definition.parameters.length !== 3
    || !isExactTickerDescriptor(definition.parameters[0])
    || !hasExactEnumDescriptor(
      definition.parameters[1],
      "direction",
      ["ABOVE", "BELOW"],
    )) {
    return false;
  }
  const threshold = definition.parameters[2];
  const expected: { id: "level_usd" | "level"; unit: WatchSignalDefinition["unit"]; minimum: string; maximum: string } = presenter.measurement === "ATR14"
    ? { id: "level_usd", unit: "USD_PER_SHARE", minimum: "0.000001", maximum: "1000000" }
    : presenter.measurement === "BOLLINGER_PERCENT_B_20_2"
      ? { id: "level", unit: "RATIO", minimum: "-100", maximum: "100" }
      : { id: "level", unit: "RATIO", minimum: "0", maximum: "100" };
  return definition.unit === expected.unit
    && threshold?.parameter_id === expected.id
    && threshold.kind === "DECIMAL"
    && threshold.required
    && threshold.unit === expected.unit
    && threshold.minimum === expected.minimum
    && threshold.maximum === expected.maximum
    && threshold.max_decimal_places === 6
    && threshold.allowed_values === undefined
    && threshold.default_value === undefined;
}

function isReviewedExactOptionContractV1(definition: WatchSignalDefinition) {
  const identity = `${definition.signal_id}:${definition.definition_version}`;
  const presenter = EXACT_OPTION_CONTRACT_PRESENTERS[identity];
  const expectedCopy = EXACT_OPTION_CONTRACT_CUSTOMER_COPY[definition.signal_id];
  if (!presenter || !expectedCopy || definition.definition_version !== 1) return false;

  const isCount = presenter.measurement === "REGULAR_SESSION_VOLUME"
    || presenter.measurement === "OPEN_INTEREST";
  const expectedFamily = presenter.measurement === "IMPLIED_VOLATILITY"
    ? "VOLATILITY"
    : "LIQUIDITY";
  const expectedMaximum = presenter.measurement === "IMPLIED_VOLATILITY"
    ? "10"
    : isCount ? "1000000000000" : "100";
  return definition.family === expectedFamily
    && exactMembers(definition.eligible_jobs, ["POSITION", "OPTIONS_ENTRY"])
    && exactMembers(definition.subject_scopes, ["OPTION_CONTRACT"])
    && exactMembers<WatchSignalOperator>(
      definition.operators,
      presenter.triggerMode === "CROSSING" ? ["gt", "lt"] : ["gte", "lte"],
    )
    && definition.unit === (isCount ? "COUNT" : "RATIO")
    && definition.customer.name === expectedCopy.name
    && definition.customer.description === expectedCopy.description
    && definition.parameters.length === 3
    && isExactOptionContractDescriptor(definition.parameters[0])
    && hasExactEnumDescriptor(
      definition.parameters[1],
      "direction",
      ["ABOVE", "BELOW"],
    )
    && hasExactNumericDescriptor(definition.parameters[2], {
      parameterId: presenter.thresholdParameter,
      kind: isCount ? "INTEGER" : "DECIMAL",
      unit: isCount ? "COUNT" : "RATIO",
      minimum: presenter.measurement === "IMPLIED_VOLATILITY" ? "0.000001" : "0",
      maximum: expectedMaximum,
      maxDecimalPlaces: isCount ? undefined : 6,
    });
}

function isReviewedStandardizedOptionChainV1(
  definition: WatchSignalDefinition,
) {
  const identity = `${definition.signal_id}:${definition.definition_version}`;
  const presenter = STANDARDIZED_OPTION_CHAIN_PRESENTERS[identity];
  const expectedCopy = STANDARDIZED_OPTION_CHAIN_CUSTOMER_COPY[definition.signal_id];
  if (!presenter || !expectedCopy || definition.definition_version !== 1) return false;

  const expectedFamily = presenter.measurement === "ATM_QUOTE_COVERAGE_30D"
    || presenter.measurement === "ATM_MEDIAN_RELATIVE_SPREAD_30D"
    ? "LIQUIDITY"
    : presenter.measurement === "RISK_REVERSAL_30D_25_DELTA"
      || presenter.measurement === "ATM_IV_TERM_SLOPE_60D_MINUS_30D"
      || presenter.measurement === "PUT_CALL_OPEN_INTEREST_7_45D"
      || presenter.measurement === "OPEN_INTEREST_CONCENTRATION_TOP5_7_45D"
      ? "OPTIONS_CHAIN"
      : "VOLATILITY";
  const minimum = presenter.measurement === "ATM_IV_30D"
    ? "0.000001"
    : presenter.measurement === "IV_RV_SPREAD_30D_20D"
      || presenter.measurement === "RISK_REVERSAL_30D_25_DELTA"
      || presenter.measurement === "ATM_IV_TERM_SLOPE_60D_MINUS_30D"
      ? "-10"
      : "0";
  const maximum = presenter.measurement === "ATM_QUOTE_COVERAGE_30D"
    || presenter.measurement === "OPEN_INTEREST_CONCENTRATION_TOP5_7_45D"
    ? "1"
    : presenter.measurement === "ATM_MEDIAN_RELATIVE_SPREAD_30D"
      ? "100"
      : presenter.measurement === "PUT_CALL_OPEN_INTEREST_7_45D"
        ? "1000"
      : "10";
  return definition.family === expectedFamily
    && exactMembers(definition.eligible_jobs, ["MARKET"])
    && exactMembers(definition.subject_scopes, ["UNDERLYING"])
    && exactMembers<WatchSignalOperator>(definition.operators, ["gte", "lte"])
    && definition.unit === "RATIO"
    && definition.customer.name === expectedCopy.name
    && definition.customer.description === expectedCopy.description
    && definition.parameters.length === 3
    && isExactTickerDescriptor(definition.parameters[0])
    && hasExactEnumDescriptor(
      definition.parameters[1],
      "direction",
      ["ABOVE", "BELOW"],
    )
    && hasExactNumericDescriptor(definition.parameters[2], {
      parameterId: "level",
      kind: "DECIMAL",
      unit: "RATIO",
      minimum,
      maximum,
      maxDecimalPlaces: 6,
    });
}

/**
 * Resolve a presenter only when the entire reviewed immutable contract matches.
 *
 * Identity alone never authorizes UI. A server-side semantic change under the
 * same ID/version fails closed until this registry receives an explicit review.
 */
export function getReviewedSignalPresenter(
  definition: WatchSignalDefinition,
): WatchSignalReleasePresenter | null {
  if (isReviewedPriceV1(definition)) return PRICE_V1_PRESENTER;
  if (isReviewedPercentageMoveV1(definition)) {
    return definition.signal_id === "underlying.price_change.prior_regular_close"
      ? PRIOR_CLOSE_PERCENTAGE_V1_PRESENTER
      : SESSION_OPEN_PERCENTAGE_V1_PRESENTER;
  }
  if (isReviewedMomentumV1(definition)) {
    return MOMENTUM_PRESENTERS[`${definition.signal_id}:${definition.definition_version}`]
      ?? null;
  }
  if (isReviewedVolatilityV1(definition)) {
    return VOLATILITY_PRESENTERS[
      `${definition.signal_id}:${definition.definition_version}`
    ] ?? null;
  }
  if (isReviewedExactOptionContractV1(definition)) {
    return EXACT_OPTION_CONTRACT_PRESENTERS[
      `${definition.signal_id}:${definition.definition_version}`
    ] ?? null;
  }
  if (isReviewedStandardizedOptionChainV1(definition)) {
    return STANDARDIZED_OPTION_CHAIN_PRESENTERS[
      `${definition.signal_id}:${definition.definition_version}`
    ] ?? null;
  }
  if (!isReviewedMovingAverageV1(definition)) return null;
  return MOVING_AVERAGE_PRESENTERS[
    `${definition.signal_id}:${definition.definition_version}`
  ] ?? null;
}

/** Return true for either reviewed Moving averages presenter kind. */
export function isMovingAveragePresenter(
  presenter: WatchSignalReleasePresenter | null,
): presenter is MovingAverageRelationshipPresenter | MovingAverageCrossoverPresenter {
  return presenter?.kind === "MOVING_AVERAGE_RELATIONSHIP"
    || presenter?.kind === "MOVING_AVERAGE_CROSSOVER";
}

/** Return true for the six reviewed, completed-daily Momentum leaves. */
export function isMomentumPresenter(
  presenter: WatchSignalReleasePresenter | null,
): presenter is MomentumRsiPresenter | MomentumMacdPresenter | MomentumRocPresenter {
  return presenter?.kind === "MOMENTUM_RSI"
    || presenter?.kind === "MOMENTUM_MACD"
    || presenter?.kind === "MOMENTUM_ROC";
}

/** Return true only for the four reviewed state-only volatility leaves. */
export function isCompletedDailyVolatilityPresenter(
  presenter: WatchSignalReleasePresenter | null,
): presenter is CompletedDailyVolatilityPresenter {
  return presenter?.kind === "COMPLETED_DAILY_VOLATILITY";
}

/** Return true only for the eight reviewed exact-contract definitions. */
export function isExactOptionContractPresenter(
  presenter: WatchSignalReleasePresenter | null,
): presenter is ExactOptionContractPresenter {
  return presenter?.kind === "EXACT_OPTION_CONTRACT";
}

/** Return true only for the seven reviewed standardized-chain definitions. */
export function isStandardizedOptionChainPresenter(
  presenter: WatchSignalReleasePresenter | null,
): presenter is StandardizedOptionChainPresenter {
  return presenter?.kind === "STANDARDIZED_OPTION_CHAIN";
}

/** Customer-only copy for one completed-daily volatility threshold. */
export function describeCompletedDailyVolatilityCondition(input: {
  ticker: string;
  measurement: CompletedDailyVolatilityMeasurement;
  direction: "ABOVE" | "BELOW" | null;
  level: string;
}) {
  const ticker = input.ticker.trim().toUpperCase() || "The ticker";
  const direction = input.direction === "ABOVE"
    ? "at or above"
    : input.direction === "BELOW" ? "at or below" : "—";
  const level = input.level || "—";
  if (input.measurement === "ATR14") {
    return `${ticker}’s completed-daily 14-day average true range is ${direction} $${level}.`;
  }
  if (input.measurement === "BOLLINGER_PERCENT_B_20_2") {
    return `${ticker}’s completed-daily Bollinger %B is ${direction} ${level}.`;
  }
  if (input.measurement === "BOLLINGER_BANDWIDTH_20_2") {
    const percent = input.level ? `${canonicalRatioToPercent(input.level)}%` : "—";
    return `${ticker}’s completed-daily Bollinger band width is ${direction} ${percent}.`;
  }
  const percent = input.level ? `${canonicalRatioToPercent(input.level)}%` : "—";
  return `${ticker}’s 20-day annualized realized volatility is ${direction} ${percent}.`;
}

/** Customer-only copy for a complete or partial Momentum rule. */
export function describeMomentumCondition(input: {
  ticker: string;
  indicator: "RSI" | "MACD" | "ROC";
  triggerMode?: "STATE" | "CROSSING" | null;
  comparison?: "SIGNAL_LINE" | "ZERO" | null;
  direction: "ABOVE" | "BELOW" | null;
  level?: string;
  levelPercent?: string;
}) {
  const ticker = input.ticker.trim().toUpperCase() || "The ticker";
  const direction = input.direction === "ABOVE" ? "above" : input.direction === "BELOW" ? "below" : "—";
  const verb = input.triggerMode === "CROSSING"
    ? `crosses ${direction}`
    : input.direction === "ABOVE" ? "is at or above" : input.direction === "BELOW" ? "is at or below" : "is —";
  if (input.indicator === "RSI") {
    return `${ticker}’s 14-day RSI ${verb} ${input.level || "—"}.`;
  }
  if (input.indicator === "ROC") {
    return `${ticker}’s 14-day rate of change ${verb} ${input.levelPercent || "—"}%.`;
  }
  return `${ticker}’s MACD line crosses ${direction} ${input.comparison === "SIGNAL_LINE" ? "its signal line" : input.comparison === "ZERO" ? "zero" : "—"}.`;
}

/** Customer copy for a complete or partial Moving averages rule. */
export function describeMovingAverageCondition(input: {
  ticker: string;
  comparison: "PRICE_TO_AVERAGE" | "AVERAGE_CROSSOVER";
  averageType: MovingAverageType | null;
  window: MovingAverageWindow | null;
  windowPair: MovingAverageWindowPair | null;
  triggerMode: MovingAverageTriggerMode | null;
  direction: MovingAverageDirection | null;
}) {
  const ticker = input.ticker.trim().toUpperCase() || "The ticker";
  const averageName = input.averageType === "SMA"
    ? "simple moving average"
    : input.averageType === "EMA"
      ? "exponential moving average"
      : "moving average";
  const direction = input.direction === "ABOVE" ? "above" : input.direction === "BELOW" ? "below" : "—";
  if (input.comparison === "AVERAGE_CROSSOVER") {
    const [fast = "—", slow = "—"] = input.windowPair?.split("_") ?? [];
    const averageShort = input.averageType ?? "moving average";
    return `Alert me when ${ticker}'s ${fast}-day ${averageShort} crosses ${direction} its ${slow}-day ${averageShort} after this Watch starts.`;
  }
  const window = input.window ?? "—";
  return input.triggerMode === "CROSSING"
    ? `Alert me when ${ticker}'s completed daily close crosses ${direction} its ${window}-day ${averageName} after this Watch starts.`
    : `Alert me when ${ticker}'s completed daily close is ${direction} its ${window}-day ${averageName}.`;
}

/** Build customer-only draft copy without exposing internal signal metadata. */
export function describeWatchSignalCondition(
  presenter: WatchSignalReleasePresenter | null,
  ticker: string,
  operator: "gte" | "lte" | null,
  threshold: string,
) {
  if (!operator) {
    return presenter?.kind === "PERCENTAGE_MOVE"
      ? "Choose Up or Down."
      : "Choose a price direction.";
  }
  const subject = ticker.trim().toUpperCase() || "The price";
  if (presenter?.kind === "PERCENTAGE_MOVE") {
    return `${subject} is ${operator === "gte" ? "up" : "down"} at least ${threshold || "—"}% from ${presenter.referencePhrase}.`;
  }
  if (presenter?.kind === "COMPLETED_DAILY_VOLATILITY") {
    return describeCompletedDailyVolatilityCondition({
      ticker,
      measurement: presenter.measurement,
      direction: operator === "gte" ? "ABOVE" : "BELOW",
      level: threshold,
    });
  }
  if (presenter?.kind === "STANDARDIZED_OPTION_CHAIN") {
    const direction = operator === "gte" ? "at or above" : "at or below";
    const value = threshold ? canonicalRatioToPercent(threshold) : "—";
    const measurement = {
      ATM_IV_30D: "standardized 30-day implied volatility",
      IV_RV_SPREAD_30D_20D: "30-day implied minus 20-day realized volatility",
      RISK_REVERSAL_30D_25_DELTA: "standardized 30-day put/call skew",
      ATM_IV_TERM_SLOPE_60D_MINUS_30D: "standardized 30-to-60-day IV slope",
      EXPECTED_MOVE_30D: "standardized 30-day expected move",
      ATM_QUOTE_COVERAGE_30D: "standardized near-ATM quote coverage",
      ATM_MEDIAN_RELATIVE_SPREAD_30D: "typical standardized near-ATM spread",
      PUT_CALL_OPEN_INTEREST_7_45D: "standardized near-term put/call open interest",
      OPEN_INTEREST_CONCENTRATION_TOP5_7_45D: "top-five open-interest concentration",
    }[presenter.measurement];
    const unit = presenter.measurement === "PUT_CALL_OPEN_INTEREST_7_45D"
      ? ""
      : presenter.measurement === "IV_RV_SPREAD_30D_20D"
      || presenter.measurement === "RISK_REVERSAL_30D_25_DELTA"
      || presenter.measurement === "ATM_IV_TERM_SLOPE_60D_MINUS_30D"
      ? " percentage points"
      : "%";
    const displayValue = presenter.measurement === "PUT_CALL_OPEN_INTEREST_7_45D"
      ? threshold || "—"
      : value;
    return `${subject}’s ${measurement} is ${direction} ${displayValue}${unit}.`;
  }
  return `${subject} ${operator === "gte" ? "reaches or rises above" : "reaches or falls below"} $${threshold || "—"}`;
}
